import React, { useId } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import { NO_COMPARISON, variation } from "../../utils/analyticsDeltas";
import { voiceConversationsLink } from "../../utils/conversationFilters";
import { formatVoiceDuration, voiceDurationBars } from "../../utils/analyticsVoice";
import InfoTip from "../charts/InfoTip";
import { formatCount, formatDecimal } from "../charts/format";
import BarsFigure from "./BarsFigure";
import KpiStrip from "./KpiStrip";
import { countText } from "./kpis";

// O cartao das mensagens de voz (D10 do item 13): as mensagens (com a variacao), as conversas que tiveram
// uma e a duracao mediana, e por baixo a distribuicao da duracao. So se desenha com `summary.voice`, e quem
// o chama decide isso (um backend antigo nao o manda). Um valor null le-se "n/d". O "Ver conversas" (D14)
// so para quem pode abrir conversas, com o chat e o periodo da pagina, e so com conversas: sem elas a lista
// abria vazia, a regra das ligacoes do CSAT.
const VoiceBlock = ({ summary, period, previousComplete, canOpenConversations }) => {
  const { translate, language } = useLanguage();
  const headingId = useId();
  const { messages, conversations, conversations_pct: pct, duration } = summary.voice;
  const title = translate("analyticsVoiceTitle");
  const kpis = [
    {
      key: "voice_messages",
      label: title,
      value: formatCount(messages.value, language),
      change: previousComplete ? variation({ kind: "count", ...messages }, language) : NO_COMPARISON,
      context: translate("analyticsKpiPrevious", { previous: formatCount(messages.previous, language) }),
    },
    {
      key: "voice_conversations",
      label: translate("analyticsVoiceConversations"),
      value: formatCount(conversations, language),
      context: pct === null ? null : translate("analyticsVoiceConversationsShare", { pct: formatDecimal(pct, language) }),
    },
    {
      key: "voice_median",
      label: translate("analyticsVoiceMedian"),
      value: formatVoiceDuration(duration.median_ms, language, translate),
      context: countText(duration.measured, "analyticsVoiceMeasured", translate, language),
    },
  ];
  return (
    <section aria-labelledby={headingId} className="space-y-3">
      <div className="flex items-center gap-1">
        <h3 id={headingId} className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>
          {title}
        </h3>
        <InfoTip text={translate("analyticsVoiceInfo")} label={translate("analyticsAbout", { title })} />
        {canOpenConversations && conversations > 0 && (
          <Link
            to={voiceConversationsLink({ chatId: summary.chat.id, period })}
            className="ml-auto text-xs underline underline-offset-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-mint)]"
            style={{ color: "var(--color-text-secondary)" }}
          >
            {translate("analyticsViewConversations")}
          </Link>
        )}
      </div>
      <KpiStrip label={translate("analyticsVoiceFigures")} kpis={kpis} />
      <BarsFigure
        title={translate("analyticsVoiceDistribution")}
        bars={voiceDurationBars(duration.histogram, translate)}
        downloadName={translate("analyticsVoiceDistribution")}
      />
    </section>
  );
};

export default VoiceBlock;
